import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";
import { PrintDesigner } from "../../components/productCustomization/PrintDesigner";
import { ThreeDPrintDesigner } from "../../components/productCustomization/ThreeDPrintDesigner";
import { productCustomizationApi } from "../../services/productCustomizationApi";
import { useAuth } from "../../context/AuthContext";
import { useStore } from "../../context/StoreContext";
import { customizationAssetApi } from "../../services/customizationAssetApi";
import { Select } from "../../components/ui/Select";
import { Text } from "../../components/ui/Text";
import {
  FaPlus,
  FaFilePdf,
  FaWhatsapp,
  FaUser,
  FaTag,
  FaSync,
  FaDownload,
  FaCheck,
  FaSave,
  FaEdit,
  FaTrash,
  FaCalculator,
} from "react-icons/fa";
import { getResizedImageUrl, ImagePresets } from "../../utils/imageUtils";
import { useStoreLogoPlaceholder } from "../../hooks/useStoreLogoPlaceholder";
import { findProductImage } from "../../utils/productImageUtils";
import { TrackingTimeline } from "../../components/order/TrackingTimeline";
import {
  calculateOriginalPriceForOrderItem,
  calculateOrderItemDiscount,
} from "../../utils/orderPricingUtils";

const openWhatsApp = (phoneNumber: string, orderNumber: string): void => {
  // Remove any non-digit characters except +
  const cleanPhone = phoneNumber.replace(/[^\d+]/g, "");
  // Remove + if present and add it back, ensure it starts with +
  const formattedPhone = cleanPhone.startsWith("+")
    ? cleanPhone
    : `+${cleanPhone}`;
  const message = encodeURIComponent(
    `Hello! I have a question about order ${orderNumber}.`,
  );
  const whatsappUrl = `https://wa.me/${formattedPhone.replace("+", "")}?text=${message}`;
  window.open(whatsappUrl, "_blank");
};

// Profit Distribution Section Component
const ProfitDistributionSection: React.FC<{
  orderId: string;
  order: any;
  onRecalculate: () => void;
}> = ({ orderId, order, onRecalculate }) => {
  const { t } = useTranslation();
  const { api } = useApi();
  const [profitDetails, setProfitDetails] = useState<any[]>([]);
  const [contributors, setContributors] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [itemAssignments, setItemAssignments] = useState<
    Record<string, Array<{ contributorId: string; roleId: string }>>
  >({});

  useEffect(() => {
    loadData();
  }, [orderId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load profit details
      const profitResponse = await api.get(
        `/profit-rules/orders/${orderId}/profit`,
      );
      setProfitDetails(profitResponse.data);

      // Load contributors and roles
      const [contributorsResponse, rolesResponse] = await Promise.all([
        api.get("/profit-rules/contributors"),
        api.get("/profit-rules/roles"),
      ]);
      setContributors(contributorsResponse.data);
      setRoles(rolesResponse.data);

      // Initialize item assignments from order items
      if (order.items) {
        const assignments: Record<
          string,
          Array<{ contributorId: string; roleId: string }>
        > = {};
        order.items.forEach((item: any) => {
          if (
            item.contributorAssignments &&
            item.contributorAssignments.length > 0
          ) {
            assignments[item._id] = item.contributorAssignments.map(
              (a: any) => ({
                contributorId: a.contributorId?._id || a.contributorId || "",
                roleId: a.roleId?._id || a.roleId || "",
              }),
            );
          } else if (
            item.product?.contributorAssignments &&
            item.product.contributorAssignments.length > 0
          ) {
            assignments[item._id] = item.product.contributorAssignments.map(
              (a: any) => ({
                contributorId: a.contributorId?._id || a.contributorId || "",
                roleId: a.roleId?._id || a.roleId || "",
              }),
            );
          }
        });
        setItemAssignments(assignments);
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("admin.orderDetail.profit.errors.loadFailed"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItemAssignments = async (itemId: string) => {
    setSaving(true);
    setError(null);
    try {
      await api.put(`/orders/admin/${orderId}/items/${itemId}`, {
        contributorAssignments: (itemAssignments[itemId] || []).filter(
          (a) => a.contributorId && a.roleId,
        ),
      });
      setEditingItem(null);
      await loadData();
      onRecalculate(); // Trigger recalculation
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("admin.orderDetail.profit.errors.updateAssignments"),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRecalculateProfit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/profit-rules/orders/${orderId}/profit/recalculate`);
      await loadData();
      onRecalculate();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("admin.orderDetail.profit.errors.recalculationFailed"),
      );
    } finally {
      setSaving(false);
    }
  };

  const addAssignment = (itemId: string) => {
    setItemAssignments({
      ...itemAssignments,
      [itemId]: [
        ...(itemAssignments[itemId] || []),
        { contributorId: "", roleId: "" },
      ],
    });
  };

  const removeAssignment = (itemId: string, index: number) => {
    const assignments = itemAssignments[itemId] || [];
    setItemAssignments({
      ...itemAssignments,
      [itemId]: assignments.filter((_, i) => i !== index),
    });
  };

  const updateAssignment = (
    itemId: string,
    index: number,
    field: "contributorId" | "roleId",
    value: string,
  ) => {
    const assignments = itemAssignments[itemId] || [];
    assignments[index] = { ...assignments[index], [field]: value };
    setItemAssignments({
      ...itemAssignments,
      [itemId]: assignments,
    });
  };

  if (loading) {
    return <Spinner />;
  }

  // Group profit details by order item
  const profitByItem = profitDetails.reduce((acc: any, detail: any) => {
    const itemId = detail.orderItemId._id || detail.orderItemId;
    if (!acc[itemId]) {
      acc[itemId] = [];
    }
    acc[itemId].push(detail);
    return acc;
  }, {});

  const isEditable = ["confirmed", "processing"].includes(order.status);

  return (
    <div className="space-y-2">
      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex justify-between items-center mb-2">
        <Text className="text-xs text-gray-600 dark:text-gray-400">
          {isEditable
            ? t("admin.orderDetail.profit.description")
            : t("admin.orderDetail.profit.viewOnlyDescription")}
        </Text>
        {isEditable && (
          <button
            onClick={handleRecalculateProfit}
            disabled={saving}
            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
            title={t("admin.orderDetail.profit.recalculate") || "Recalculate"}
          >
            {saving ? <Spinner size="sm" /> : <FaCalculator size={14} />}
          </button>
        )}
      </div>

      {order.items?.map((item: any) => {
        const itemId = item._id;
        const assignments = itemAssignments[itemId] || [];
        const itemProfits = profitByItem[itemId] || [];
        const isEditing = editingItem === itemId;

        return (
          <div
            key={itemId}
            className="border border-gray-200 dark:border-gray-700 rounded p-2"
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <Text className="text-sm font-medium">{item.productTitle}</Text>
                {item.variantName && (
                  <Text className="text-xs text-gray-500">
                    {item.variantName}
                  </Text>
                )}
              </div>
              {!isEditing && isEditable && (
                <button
                  onClick={() => setEditingItem(itemId)}
                  className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300"
                  title={
                    t("admin.orderDetail.profit.editMappings") ||
                    "Edit Mappings"
                  }
                >
                  <FaEdit size={12} />
                </button>
              )}
            </div>

            {isEditing ? (
              <div className="space-y-2">
                {assignments.map((assignment, index) => (
                  <div key={index} className="flex gap-1.5 items-end">
                    <Select
                      label={t("admin.orderDetail.profit.contributor")}
                      value={assignment.contributorId}
                      onChange={(e) =>
                        updateAssignment(
                          itemId,
                          index,
                          "contributorId",
                          e.target.value,
                        )
                      }
                      className="flex-1 text-xs"
                      options={[
                        {
                          value: "",
                          label: t(
                            "admin.orderDetail.profit.selectContributor",
                          ),
                        },
                        ...contributors.map((c) => ({
                          value: c._id,
                          label: c.name,
                        })),
                      ]}
                    />
                    <Select
                      label={t("admin.orderDetail.profit.role")}
                      value={assignment.roleId}
                      onChange={(e) =>
                        updateAssignment(
                          itemId,
                          index,
                          "roleId",
                          e.target.value,
                        )
                      }
                      className="flex-1 text-xs"
                      options={[
                        {
                          value: "",
                          label: t("admin.orderDetail.profit.selectRole"),
                        },
                        ...roles.map((r) => ({
                          value: r._id,
                          label: r.name,
                        })),
                      ]}
                    />
                    <button
                      onClick={() => removeAssignment(itemId, index)}
                      className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 rounded border border-red-300"
                      title={t("common.delete") || "Delete"}
                    >
                      <FaTrash size={12} />
                    </button>
                  </div>
                ))}
                <div className="flex gap-1.5">
                  <button
                    onClick={() => addAssignment(itemId)}
                    className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 flex items-center gap-1"
                  >
                    <FaPlus size={10} />
                    <span>{t("admin.orderDetail.profit.addAssignment")}</span>
                  </button>
                  <button
                    onClick={() => handleUpdateItemAssignments(itemId)}
                    disabled={saving}
                    className="px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                  >
                    {saving ? <Spinner size="sm" /> : t("common.save")}
                  </button>
                  <button
                    onClick={() => setEditingItem(null)}
                    className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <Text className="text-xs font-medium text-gray-600">
                  {t("admin.orderDetail.profit.currentMappings")}:
                </Text>
                {assignments.length === 0 ? (
                  <Text className="text-xs text-gray-500">
                    {t("admin.orderDetail.profit.noMappings")}
                  </Text>
                ) : (
                  <div className="space-y-0.5">
                    {assignments.map((assignment, index) => {
                      const contributor = contributors.find(
                        (c) => c._id === assignment.contributorId,
                      );
                      const role = roles.find(
                        (r) => r._id === assignment.roleId,
                      );
                      return (
                        <Text key={index} className="text-xs">
                          {contributor?.name ||
                            t(
                              "admin.orderDetail.profit.unknownContributor",
                            )}{" "}
                          -{" "}
                          {role?.name ||
                            t("admin.orderDetail.profit.unknownRole")}
                        </Text>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {itemProfits.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <Text className="text-xs font-medium mb-1 text-gray-600">
                  {t("admin.orderDetail.profit.breakdown")}:
                </Text>
                <div className="space-y-0.5 text-xs">
                  {itemProfits.map((profit: any, index: number) => (
                    <div key={index} className="flex justify-between">
                      <Text>
                        {profit.isStoreOwner
                          ? t("admin.orderDetail.profit.storeOwner")
                          : `${profit.contributorId?.name || t("admin.orderDetail.profit.unknownContributor")} (${profit.roleId?.name || t("admin.orderDetail.profit.unknownRole")})`}
                      </Text>
                      <Text>
                        {profit.currency} {profit.profitAmount.toFixed(2)} (
                        {profit.profitPercentage.toFixed(1)}%)
                      </Text>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const OrderDetail: React.FC = () => {
  const { t } = useTranslation();
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { api } = useApi();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  const [order, setOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [updating, setUpdating] = useState(false);
  const [viewingCustomization, setViewingCustomization] = useState<{
    customizationId: string;
    productId: string;
    canvasSize?: string;
    type: "2d-print" | "3d-print";
  } | null>(null);
  const [customizationData, setCustomizationData] = useState<any>(null);
  const [loadingCustomization, setLoadingCustomization] = useState(false);
  const [productData, setProductData] = useState<any>(null);
  const { user } = useAuth();
  const { isPluginEnabled } = useStore();
  const isProfitPluginEnabled = isPluginEnabled("profit-rules");
  const isCustomizationPluginEnabled = isPluginEnabled("product-customization");
  const [backgroundAssets, setBackgroundAssets] = useState<Map<string, string>>(
    new Map(),
  );
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const isCarrierPluginEnabled = isPluginEnabled(
    "shipping-carrier-integration",
  );
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [syncingTracking, setSyncingTracking] = useState(false);
  const [downloadingLabel, setDownloadingLabel] = useState(false);
  const [editingItemNote, setEditingItemNote] = useState<string | null>(null);
  const [itemNotes, setItemNotes] = useState<Record<string, string>>({});
  const [savingNote, setSavingNote] = useState<string | null>(null);
  const hasSyncedTrackingRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (orderId) {
      fetchOrder();
      hasSyncedTrackingRef.current = null; // Reset sync flag when orderId changes
    }
  }, [orderId]);

  // Auto-fetch tracking details if order has carrier tracking but no events
  // Only sync once per order load to avoid infinite loops
  useEffect(() => {
    if (
      order &&
      orderId &&
      order.shippingCarrierTrackingNumber &&
      (!order.trackingEvents || order.trackingEvents.length === 0) &&
      order.status !== "cancelled" &&
      isCarrierPluginEnabled &&
      hasSyncedTrackingRef.current !== orderId
    ) {
      // Auto-sync tracking when order is loaded (only once per orderId)
      const autoSyncTracking = async () => {
        try {
          hasSyncedTrackingRef.current = orderId; // Mark as synced before making the call
          await api.get(`/orders/admin/${orderId}/tracking`);
          // Refresh order to get updated tracking events
          await fetchOrder();
        } catch (err) {
          // Silently fail - tracking might not be available yet
          hasSyncedTrackingRef.current = orderId; // Mark as synced even on error to prevent retries
        }
      };
      autoSyncTracking();
    }
  }, [order, orderId, isCarrierPluginEnabled, api]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/admin/${orderId}`);
      const orderData = response.data;
      setOrder(orderData);

      // Initialize item notes from order items
      const notesMap: Record<string, string> = {};
      if (orderData.items) {
        orderData.items.forEach((item: any) => {
          if (item.note) {
            notesMap[item._id] = item.note;
          }
        });
      }
      setItemNotes(notesMap);

      setTrackingNumber(orderData.trackingNumber || "");
      setNotes(orderData.notes || "");
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("admin.orderDetail.errors.fetch"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveItemNote = async (itemId: string, note: string) => {
    try {
      setSavingNote(itemId);
      await api.put(`/orders/admin/${orderId}/items/${itemId}/note`, {
        note: note.trim() || undefined,
      });
      setItemNotes((prev) => ({
        ...prev,
        [itemId]: note.trim() || "",
      }));
      setEditingItemNote(null);
      fetchOrder(); // Refresh order to get updated data
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("admin.orderDetail.errors.saveNote"),
      );
    } finally {
      setSavingNote(null);
    }
  };

  /* 
{
   setLoading(true);
   setError(null);
   const response = await api.get(`/orders/admin/${orderId}`);
   setOrder(response.data);
   setTrackingNumber(response.data.trackingNumber || "");
   setNotes(response.data.notes || "");
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.orderDetail.errors.fetch"));
  } finally {
   setLoading(false);
  }
 };
*/

  const handleUpdateTracking = async () => {
    try {
      setUpdating(true);
      await api.put(`/orders/admin/${orderId}/tracking`, { trackingNumber });
      await fetchOrder();
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("admin.orderDetail.errors.updateTracking"),
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdateNotes = async () => {
    try {
      setUpdating(true);
      await api.put(`/orders/admin/${orderId}/notes`, { notes });
      await fetchOrder();
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("admin.orderDetail.errors.updateNotes"),
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!orderId) return;

    try {
      setLoadingInvoice(true);
      setError(null);

      // Download invoice PDF from bucket
      const response = await api.get(`/invoices/admin/order/${orderId}/pdf`, {
        responseType: "blob", // Get as blob for PDF download
      });

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `invoice-${order?.orderNumber || orderId}.pdf`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to download invoice. Please try again.",
      );
    } finally {
      setLoadingInvoice(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      setUpdating(true);
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      await fetchOrder();
    } catch (err: any) {
      setError(
        err.response?.data?.error || t("admin.orderDetail.errors.updateStatus"),
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleGenerateShippingLabel = async () => {
    if (!orderId) return;
    try {
      setGeneratingLabel(true);
      setError(null);
      const response = await api.post(
        `/orders/admin/${orderId}/shipping-label`,
      );
      await fetchOrder();
      // Show success message or handle label URL
      if (response.data.label?.labelUrl) {
        // Optionally open label in new tab
        window.open(response.data.label.labelUrl, "_blank");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("admin.orderDetail.errors.generateLabel") ||
          "Failed to generate shipping label",
      );
    } finally {
      setGeneratingLabel(false);
    }
  };

  const handleSyncTracking = async () => {
    if (!orderId) return;
    try {
      setSyncingTracking(true);
      setError(null);
      const response = await api.get(`/orders/admin/${orderId}/tracking`);
      // Update order state with new tracking events if returned
      if (response.data?.trackingEvents) {
        setOrder((prevOrder: any) => ({
          ...prevOrder,
          trackingEvents: response.data.trackingEvents,
        }));
      } else {
        // Refresh order to get updated tracking events
        await fetchOrder();
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("admin.orderDetail.errors.syncTracking") ||
          "Failed to sync tracking information",
      );
    } finally {
      setSyncingTracking(false);
    }
  };

  const handleDownloadShippingLabel = async () => {
    if (!orderId) return;

    try {
      setDownloadingLabel(true);
      setError(null);

      // Download shipping label PDF
      const response = await api.get(
        `/orders/admin/${orderId}/shipping-label/pdf`,
        {
          responseType: "blob", // Get as blob for PDF download
        },
      );

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filename = `shipping-label-${order?.orderNumber || orderId}${order?.shippingCarrierTrackingNumber ? `-${order.shippingCarrierTrackingNumber}` : ""}.pdf`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to download shipping label. Please try again.",
      );
    } finally {
      setDownloadingLabel(false);
    }
  };

  // Compute background images and available shapes from product data (same logic as ProductDetail)
  const backgroundImages = useMemo(() => {
    // Only use backgrounds from the assets library (selected backgrounds for the product)
    if (
      productData?.availableAssetIds &&
      productData.availableAssetIds.length > 0
    ) {
      return productData.availableAssetIds
        .filter((asset: any) => asset && asset.type === "background")
        .map((asset: any) => asset.url);
    }
    return [];
  }, [productData]);

  const availableShapes = useMemo(() => {
    if (
      productData?.availableAssetIds &&
      productData.availableAssetIds.length > 0
    ) {
      return productData.availableAssetIds
        .filter((asset: any) => asset && asset.type === "shape")
        .map((asset: any) => ({
          id: asset._id,
          name: asset.name,
          url: asset.url,
          category: asset.category,
        }));
    }
    return [];
  }, [productData]);

  // Fetch background assets to get originalUrl mapping for admins
  // Fetch when productData is loaded OR when customizationData has a backgroundImage
  useEffect(() => {
    const fetchAssets = async () => {
      // Fetch if we have background images from product OR if we have a customization with background
      if (
        backgroundImages.length === 0 &&
        !customizationData?.backgroundImage
      ) {
        return;
      }

      try {
        const assets = await customizationAssetApi.getAssets("background");
        const urlMap = new Map<string, string>();

        // Helper to extract filename from URL for matching
        const getFilename = (url: string): string => {
          try {
            const urlObj = new URL(url);
            return urlObj.pathname.split("/").pop() || "";
          } catch {
            return url.split("/").pop() || "";
          }
        };

        // Create mapping of any URL (watermarked or original) to originalUrl
        assets.forEach((asset) => {
          if (asset.originalUrl) {
            // For admins, asset.url is the original, and watermarkedUrl (if present) is the watermarked
            // For users, asset.url is the watermarked
            const watermarkedUrl = (asset as any).watermarkedUrl || asset.url; // Use watermarkedUrl if available (for admins), otherwise url
            const originalUrl = asset.originalUrl;

            // Map both watermarked and original URLs to the original
            urlMap.set(watermarkedUrl, originalUrl);
            urlMap.set(originalUrl, originalUrl);
            urlMap.set(asset.url, originalUrl); // Also map the url field (which is original for admins)

            // Also map by filename for better matching
            const watermarkedFilename = getFilename(watermarkedUrl);
            const originalFilename = getFilename(originalUrl);
            const urlFilename = getFilename(asset.url);
            if (watermarkedFilename)
              urlMap.set(watermarkedFilename, originalUrl);
            if (originalFilename) urlMap.set(originalFilename, originalUrl);
            if (urlFilename) urlMap.set(urlFilename, originalUrl);
          } else {
            // For assets without originalUrl, url is already the original (for admins)
            urlMap.set(asset.url, asset.url);
          }
        });
        setBackgroundAssets(urlMap);
      } catch (error) {
        console.warn("Failed to fetch background assets:", error);
      }
    };

    fetchAssets();
  }, [backgroundImages.length, customizationData?.backgroundImage]);

  // Helper to get original URL for a background image (for admins)
  const getOriginalBackgroundUrl = useCallback(
    (url: string | undefined): string | undefined => {
      if (!url) return undefined;

      const isAdmin =
        user &&
        (user.role === "admin" ||
          user.role === "store_admin" ||
          user.role === "co_admin");
      if (!isAdmin) {
        return url; // Regular users get watermarked version
      }

      // For admins, try to get originalUrl from the map
      const originalUrl = backgroundAssets.get(url);
      if (originalUrl) {
        return originalUrl;
      }

      // If not found in map, check by filename
      const getFilename = (urlStr: string): string => {
        try {
          const urlObj = new URL(urlStr);
          return urlObj.pathname.split("/").pop() || "";
        } catch {
          return urlStr.split("/").pop() || "";
        }
      };

      const filename = getFilename(url);
      const originalByFilename = backgroundAssets.get(filename);
      if (originalByFilename) {
        return originalByFilename;
      }

      // If no mapping found, assume url is already original (for admins, backend returns original)
      return url;
    },
    [user, backgroundAssets],
  );

  // Update background image when assets are loaded (convert watermarked to original for admins)
  const [originalBackgroundImage, setOriginalBackgroundImage] = useState<
    string | undefined
  >(undefined);

  useEffect(() => {
    if (customizationData?.backgroundImage) {
      if (backgroundAssets.size > 0) {
        // Assets are loaded, convert the URL
        const originalUrl = getOriginalBackgroundUrl(
          customizationData.backgroundImage,
        );
        setOriginalBackgroundImage(originalUrl);
      } else {
        // Assets not loaded yet, use the original URL for now (will update when assets load)
        // For admins, we'll try to convert it once assets are loaded
        const isAdmin =
          user &&
          (user.role === "admin" ||
            user.role === "store_admin" ||
            user.role === "co_admin");
        if (isAdmin) {
          // Keep the watermarked URL temporarily, will be updated when assets load
          setOriginalBackgroundImage(customizationData.backgroundImage);
        } else {
          setOriginalBackgroundImage(customizationData.backgroundImage);
        }
      }
    } else {
      setOriginalBackgroundImage(undefined);
    }
  }, [
    customizationData?.backgroundImage,
    backgroundAssets.size,
    getOriginalBackgroundUrl,
    user,
  ]);

  // Get current shipping status from tracking events
  const getShippingStatus = () => {
    if (!order?.trackingEvents || order.trackingEvents.length === 0) {
      // Fallback to order status if no tracking events
      if (order?.status === "shipped" || order?.status === "delivered") {
        return {
          status: order.status,
          description: order.status === "delivered" 
            ? "Order has been delivered" 
            : "Order is on the way",
        };
      }
      return null;
    }

    // Get the most recent tracking event (they should be sorted by timestamp)
    const latestEvent = order.trackingEvents[0];
    const statusLower = latestEvent.status?.toLowerCase() || "";

    // Map tracking status to user-friendly messages
    if (statusLower.includes("delivered")) {
      return {
        status: "delivered",
        description: latestEvent.description || "Order has been delivered",
        location: latestEvent.location,
      };
    }
    if (statusLower.includes("out_for_delivery") || statusLower.includes("out for delivery")) {
      return {
        status: "out_for_delivery",
        description: latestEvent.description || "Order is out for delivery",
        location: latestEvent.location,
      };
    }
    if (statusLower.includes("in_transit") || statusLower.includes("transit") || statusLower.includes("shipped")) {
      return {
        status: "in_transit",
        description: latestEvent.description || "Order is in transit",
        location: latestEvent.location,
      };
    }
    if (statusLower.includes("processing") || statusLower.includes("preparing")) {
      return {
        status: "processing",
        description: latestEvent.description || "Order is being prepared",
        location: latestEvent.location,
      };
    }

    return {
      status: latestEvent.status || "unknown",
      description: latestEvent.description || latestEvent.status,
      location: latestEvent.location,
    };
  };

  const getShippingStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes("delivered")) {
      return "bg-green-100 text-green-800 border-green-200";
    }
    if (statusLower.includes("out_for_delivery") || statusLower.includes("out for delivery")) {
      return "bg-blue-100 text-blue-800 border-blue-200";
    }
    if (statusLower.includes("in_transit") || statusLower.includes("transit") || statusLower.includes("shipped")) {
      return "bg-blue-50 text-blue-700 border-blue-200";
    }
    if (statusLower.includes("processing") || statusLower.includes("preparing")) {
      return "bg-yellow-50 text-yellow-700 border-yellow-200";
    }
    return "bg-gray-100 text-gray-800 border-gray-200";
  };

  const getCarrierName = (provider?: string) => {
    if (!provider) return null;
    const providerUpper = provider.toUpperCase();
    switch (providerUpper) {
      case "FLEETRUNNR":
        return "Fleetrunnr";
      case "DHL":
        return "DHL";
      case "GLS":
        return "GLS";
      case "DPD":
        return "DPD";
      default:
        return provider.charAt(0).toUpperCase() + provider.slice(1);
    }
  };

  const shippingStatus = order ? getShippingStatus() : null;

  if (loading) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Spinner size="lg" />
        </Stack>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <Alert variant="error">{t("admin.orderDetail.notFound")}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="md">
        <div className="flex items-center justify-between">
          <div>
            <Heading size="xl" className="mb-1">
              {t("admin.orderDetail.title")}: {order.orderNumber}
            </Heading>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  order.status === "delivered"
                    ? "bg-green-100 text-green-800"
                    : order.status === "shipped"
                      ? "bg-blue-100 text-blue-800"
                      : order.status === "processing"
                        ? "bg-yellow-100 text-yellow-800"
                        : order.status === "confirmed"
                          ? "bg-blue-100 text-blue-800"
                          : order.status === "pending"
                            ? "bg-gray-100 text-gray-800"
                            : order.status === "cancelled"
                              ? "bg-red-100 text-red-800"
                              : order.status === "refunded"
                                ? "bg-purple-100 text-purple-800"
                                : "bg-gray-100 text-gray-800"
                }`}
              >
                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
              </span>
              <span>•</span>
              <span>
                {new Date(
                  order.customOrderDate || order.createdAt,
                ).toLocaleDateString()}
              </span>
            </div>
          </div>
          <div className="flex gap-1.5">
            <button
              onClick={handleGenerateInvoice}
              disabled={loadingInvoice}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
              title={
                t("admin.orderDetail.generateInvoice") || "Download Invoice"
              }
            >
              <FaFilePdf size={16} />
            </button>
            {order.locationId &&
              typeof order.locationId === "object" &&
              order.locationId.whatsappPhoneNumber && (
                <button
                  onClick={() =>
                    openWhatsApp(
                      order.locationId.whatsappPhoneNumber,
                      order.orderNumber,
                    )
                  }
                  className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded border border-green-300"
                  title="Contact via WhatsApp"
                >
                  <FaWhatsapp size={16} />
                </button>
              )}
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2 space-y-3">
            {/* Order Notes - Compact, above items */}
            {order.notes && (
              <Card padding="sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <Heading size="sm" className="text-base mb-1">
                      {t("admin.orderDetail.customerNotes")}
                    </Heading>
                    <div className="bg-gray-50 p-2 rounded text-xs whitespace-pre-wrap text-gray-700">
                      {order.notes}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <Card padding="sm">
              <Stack spacing="sm">
                <Heading size="sm" className="text-base">
                  {t("admin.orderDetail.orderItems")}
                </Heading>
                <div className="space-y-2">
                  {order.items?.map((item: any, index: number) => {
                    const productTitle =
                      item.productTitle ||
                      item.product?.title ||
                      t("admin.orderDetail.product");
                    const variantName = item.variantName || item.variant?.name;

                    // Calculate original price dynamically based on product pricing layers
                    const originalPrice = calculateOriginalPriceForOrderItem(
                      item.product,
                      item.selectedVariantOptions,
                      order.locationId,
                    );

                    // Calculate discount information
                    const discountInfo = calculateOrderItemDiscount(
                      item.product,
                      item.price,
                      item.selectedVariantOptions,
                      order.locationId,
                    );

                    // Find product image based on variant options
                    const image =
                      item.product &&
                      item.product.images &&
                      item.product.images.length > 0
                        ? findProductImage(
                            item.product,
                            item.selectedVariantOptions,
                          )
                        : undefined;
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.checkoutReview,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const productUrl = item.product
                      ? `/products/${item.product._id}`
                      : null;
                    return (
                      <div
                        key={item._id || index}
                        className="flex gap-2 p-2 border border-gray-200 rounded"
                      >
                        {productUrl ? (
                          <Link
                            to={productUrl}
                            className="w-12 h-12 object-cover rounded flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <img
                              src={imageUrl}
                              alt={productTitle}
                              className="w-12 h-12 object-cover rounded flex-shrink-0"
                            />
                          </Link>
                        ) : (
                          <img
                            src={imageUrl}
                            alt={productTitle}
                            className="w-12 h-12 object-cover rounded flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          {productUrl ? (
                            <Link
                              to={productUrl}
                              className="font-medium text-sm hover:text-blue-600 transition-colors"
                            >
                              {productTitle}
                            </Link>
                          ) : (
                            <div className="font-medium text-sm">
                              {productTitle}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                            {variantName && (
                              <span>
                                {t("admin.orderDetail.variant")}: {variantName}
                              </span>
                            )}
                            {item.sku && (
                              <span>
                                {t("admin.orderDetail.sku")}: {item.sku}
                              </span>
                            )}
                            <span>
                              {t("admin.orderDetail.quantity")}: {item.quantity}
                            </span>
                          </div>
                          {item.customization &&
                            isCustomizationPluginEnabled &&
                            (() => {
                              const customizationId =
                                typeof item.customization === "string"
                                  ? item.customization
                                  : item.customization._id;
                              const customizationObj =
                                typeof item.customization === "object"
                                  ? item.customization
                                  : null;
                              const customizationType =
                                customizationObj?.customizationType ||
                                item.product?.customizationType;
                              const productId =
                                item.product?._id || item.product;

                              const handleOpenInDesigner = async () => {
                                if (
                                  customizationType === "2d-print" &&
                                  customizationId
                                ) {
                                  setLoadingCustomization(true);
                                  try {
                                    // Fetch both customization and product data
                                    const [customization, productResponse] =
                                      await Promise.all([
                                        productCustomizationApi.getCustomizationById(
                                          customizationId,
                                        ),
                                        api.get(`/products/admin/${productId}`),
                                      ]);

                                    if (customization && productResponse.data) {
                                      setCustomizationData(customization);
                                      setProductData(productResponse.data);
                                      setViewingCustomization({
                                        customizationId,
                                        productId: productId as string,
                                        canvasSize: item.product?.canvasSize,
                                        type: "2d-print",
                                      });
                                    }
                                  } catch (err) {
                                    setError(
                                      t(
                                        "admin.orderDetail.errors.loadCustomization",
                                      ),
                                    );
                                  } finally {
                                    setLoadingCustomization(false);
                                  }
                                } else if (
                                  customizationType === "3d-print" &&
                                  customizationId
                                ) {
                                  setLoadingCustomization(true);
                                  try {
                                    // Fetch both customization and product data
                                    const [customization, productResponse] =
                                      await Promise.all([
                                        productCustomizationApi.getCustomizationById(
                                          customizationId,
                                        ),
                                        api.get(`/products/admin/${productId}`),
                                      ]);

                                    if (customization && productResponse.data) {
                                      setCustomizationData(customization);
                                      setProductData(productResponse.data);
                                      setViewingCustomization({
                                        customizationId,
                                        productId: productId as string,
                                        type: "3d-print",
                                      });
                                    }
                                  } catch (err) {
                                    setError(
                                      t(
                                        "admin.orderDetail.errors.loadCustomization",
                                      ),
                                    );
                                  } finally {
                                    setLoadingCustomization(false);
                                  }
                                }
                              };

                              return (
                                <div className="text-sm text-gray-500 mt-2">
                                  <span className="font-medium">
                                    {t("admin.orderDetail.customizations")}:
                                  </span>
                                  <div className="mt-2">
                                    {(customizationType === "2d-print" ||
                                      customizationType === "3d-print") && (
                                      <img
                                        src={
                                          customizationObj?.previewImage ||
                                          customizationObj?.modelPreviewImage
                                        }
                                        alt="Customization preview"
                                        className="w-24 h-24 object-contain border border-gray-200 rounded cursor-pointer hover:opacity-80 transition-opacity mt-1"
                                        onClick={handleOpenInDesigner}
                                        title={
                                          loadingCustomization
                                            ? t("common.loading")
                                            : t(
                                                "admin.orderDetail.openInDesigner",
                                              ) || "Click to open in designer"
                                        }
                                      />
                                    )}
                                    {!customizationObj && (
                                      <button
                                        onClick={handleOpenInDesigner}
                                        disabled={loadingCustomization}
                                        className="text-xs text-blue-600 hover:text-blue-800 underline mt-1 disabled:opacity-50"
                                      >
                                        {loadingCustomization
                                          ? t("common.loading")
                                          : t(
                                              "admin.orderDetail.viewCustomization",
                                            )}
                                      </button>
                                    )}
                                    {item.customizationData &&
                                      Object.keys(item.customizationData)
                                        .length > 0 && (
                                        <div className="mt-2 text-xs">
                                          <ul className="list-disc list-inside">
                                            {Object.entries(
                                              item.customizationData,
                                            ).map(([key, value]) => (
                                              <li key={key}>
                                                {key}: {String(value)}
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              );
                            })()}

                          {/* Item Note Section */}
                          <div className="mt-2 border-t border-gray-200 pt-2">
                            {editingItemNote === item._id ? (
                              <div className="space-y-2">
                                <textarea
                                  value={itemNotes[item._id] || ""}
                                  onChange={(e) =>
                                    setItemNotes((prev) => ({
                                      ...prev,
                                      [item._id]: e.target.value,
                                    }))
                                  }
                                  placeholder={
                                    t(
                                      "admin.orderDetail.itemNotePlaceholder",
                                    ) || "Add a note for this item..."
                                  }
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  rows={3}
                                />
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() =>
                                      handleSaveItemNote(
                                        item._id,
                                        itemNotes[item._id] || "",
                                      )
                                    }
                                    disabled={savingNote === item._id}
                                  >
                                    {savingNote === item._id ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <FaSave size={12} />
                                    )}
                                    <span className="ml-1">
                                      {t("common.save")}
                                    </span>
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingItemNote(null);
                                      setItemNotes((prev) => ({
                                        ...prev,
                                        [item._id]: item.note || "",
                                      }));
                                    }}
                                    disabled={savingNote === item._id}
                                  >
                                    {t("common.cancel")}
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1">
                                  {itemNotes[item._id] || item.note ? (
                                    <div className="bg-blue-50 border border-blue-200 rounded p-2 text-xs text-gray-700 whitespace-pre-wrap">
                                      <div className="font-medium text-blue-700 mb-1">
                                        {t("admin.orderDetail.itemNote") ||
                                          "Note:"}
                                      </div>
                                      {itemNotes[item._id] || item.note}
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-400 italic">
                                      {t("admin.orderDetail.noItemNote") ||
                                        "No note added"}
                                    </div>
                                  )}
                                </div>
                                <button
                                  onClick={() => {
                                    setEditingItemNote(item._id);
                                    if (!itemNotes[item._id] && item.note) {
                                      setItemNotes((prev) => ({
                                        ...prev,
                                        [item._id]: item.note,
                                      }));
                                    }
                                  }}
                                  className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 flex-shrink-0"
                                  title={
                                    t("admin.orderDetail.editItemNote") ||
                                    "Edit note"
                                  }
                                >
                                  <FaEdit size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-medium text-gray-900">
                            {order.currency || "$"}
                            {(item.price * item.quantity).toFixed(2)}
                          </div>
                          {/* Show original price if discounted */}
                          {originalPrice > item.price && (
                            <div className="text-xs text-gray-500 line-through">
                              {order.currency || "$"}
                              {(originalPrice * item.quantity).toFixed(2)}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            {order.currency || "$"}
                            {item.price.toFixed(2)} each
                            {/* Show discount info */}
                            {discountInfo.discountPerUnit > 0 && (
                              <div className="text-green-600 text-xs mt-1">
                                <div>
                                  Discount: -
                                  {discountInfo.discountPerUnit.toFixed(2)}{" "}
                                  {order.currency || "$"}
                                  {discountInfo.discountType ===
                                    "percentage" && (
                                    <span className="text-xs">
                                      ({discountInfo.discountValue || 0}%)
                                    </span>
                                  )}
                                </div>
                                <div className="text-gray-500 text-xs">
                                  Applied: Product level
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Stack>
            </Card>

            {/* Profit Distribution Section - Show when status is confirmed, processing, or delivered and plugin is enabled */}
            {isProfitPluginEnabled &&
              (order.status === "confirmed" ||
                order.status === "processing" ||
                order.status === "delivered") && (
                <Card padding="sm">
                  <Stack spacing="sm">
                    <Heading size="sm" className="text-base">
                      {t("admin.orderDetail.profit.title")}
                    </Heading>
                    <ProfitDistributionSection
                      orderId={orderId!}
                      order={order}
                      onRecalculate={fetchOrder}
                    />
                  </Stack>
                </Card>
              )}

            <Card padding="sm">
              <Stack spacing="sm">
                <Heading size="sm" className="text-base">
                  Customer & Addresses
                </Heading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {order.user && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-medium text-gray-500">
                          {t("admin.orderDetail.customerInformation")}
                        </div>
                        <button
                          onClick={() =>
                            navigate(`/admin/users/${order.user._id}`)
                          }
                          className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                          title={
                            t("admin.orderDetail.viewUserProfile") ||
                            "View User Profile"
                          }
                        >
                          <FaUser size={12} />
                        </button>
                      </div>
                      <div className="text-xs text-gray-700 space-y-0.5">
                        <div className="font-medium">{order.user.name}</div>
                        <div className="text-gray-600">{order.email}</div>
                      </div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs font-medium text-gray-500 mb-1">
                      {t("admin.orderDetail.shippingAddress")}
                    </div>
                    {order.shippingAddress && (
                      <div className="text-xs text-gray-700 space-y-0.5">
                        <div className="font-medium">
                          {order.shippingAddress.firstName}{" "}
                          {order.shippingAddress.lastName}
                        </div>
                        <div>{order.shippingAddress.address1}</div>
                        {order.shippingAddress.address2 && (
                          <div>{order.shippingAddress.address2}</div>
                        )}
                        <div>
                          {order.shippingAddress.city},{" "}
                          {order.shippingAddress.province}{" "}
                          {order.shippingAddress.postalCode}
                        </div>
                        <div>{order.shippingAddress.country}</div>
                        {order.shippingAddress.phone && (
                          <div className="text-gray-500">
                            {order.shippingAddress.phone}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {order.billingAddress &&
                    order.billingAddress._id !== order.shippingAddress?._id && (
                      <div className={order.user ? "sm:col-span-2" : ""}>
                        <div className="text-xs font-medium text-gray-500 mb-1">
                          {t("admin.orderDetail.billingAddress")}
                        </div>
                        <div className="text-xs text-gray-700 space-y-0.5">
                          <div className="font-medium">
                            {order.billingAddress.firstName}{" "}
                            {order.billingAddress.lastName}
                          </div>
                          <div>{order.billingAddress.address1}</div>
                          {order.billingAddress.address2 && (
                            <div>{order.billingAddress.address2}</div>
                          )}
                          <div>
                            {order.billingAddress.city},{" "}
                            {order.billingAddress.province}{" "}
                            {order.billingAddress.postalCode}
                          </div>
                          <div>{order.billingAddress.country}</div>
                        </div>
                      </div>
                    )}
                </div>
              </Stack>
            </Card>
          </div>

          <div className="space-y-3">
            <Card padding="sm">
              <Stack spacing="sm">
                <Heading size="sm" className="text-base">
                  {t("admin.orderDetail.orderStatus")}
                </Heading>
                <select
                  value={order.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded"
                  disabled={updating}
                >
                  <option value="pending">{t("orders.pending")}</option>
                  <option value="confirmed">
                    {t("admin.orderDetail.confirmed")}
                  </option>
                  <option value="processing">
                    {t("admin.payments.processing")}
                  </option>
                  <option value="shipped">
                    {t("admin.orderDetail.shipped")}
                  </option>
                  <option value="delivered">
                    {t("admin.orderDetail.delivered")}
                  </option>
                  <option value="cancelled">
                    {t("admin.orderDetail.cancelled")}
                  </option>
                  <option value="refunded">
                    {t("admin.payments.refunded")}
                  </option>
                </select>
                <div className="pt-2 mt-2 border-t border-gray-100">
                  <Heading size="sm" className="text-base mb-2">
                    {t("admin.orderDetail.paymentInformation")}
                  </Heading>
                  <div className="space-y-1.5 text-sm">
                    {order.paymentMethod && (
                      <div>
                        <span className="font-medium">
                          {t("admin.orderDetail.paymentMethod")}:
                        </span>{" "}
                        <span className="capitalize">
                          {order.paymentMethod.replace(/_/g, " ")}
                        </span>
                      </div>
                    )}
                    {order.payment ? (
                      <>
                        <div>
                          <span className="font-medium">
                            {t("admin.orderDetail.paymentStatus")}:
                          </span>{" "}
                          <span
                            className={`px-2 py-1 text-xs font-medium ${
                              order.payment.status === "completed"
                                ? "bg-green-100 text-green-800"
                                : order.payment.status === "processing"
                                  ? "bg-blue-100 text-blue-800"
                                  : order.payment.status === "pending"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : order.payment.status === "failed"
                                      ? "bg-red-100 text-red-800"
                                      : order.payment.status === "refunded"
                                        ? "bg-purple-100 text-purple-800"
                                        : order.payment.status === "authorized"
                                          ? "bg-blue-100 text-blue-800"
                                          : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {t(
                              `admin.orders.paymentStatus.${order.payment.status}`,
                            ) || order.payment.status}
                          </span>
                          {order.payment.authorizationStatus && (
                            <>
                              <span className="mx-2 text-gray-400">•</span>
                              <span className="text-xs text-gray-500">
                                {t("admin.orders.authorizationStatus")}:{" "}
                                {t(
                                  `admin.orders.authorizationStatusValues.${order.payment.authorizationStatus}`,
                                ) || order.payment.authorizationStatus}
                              </span>
                            </>
                          )}
                        </div>
                        <div>
                          <span className="font-medium">
                            {t("admin.orderDetail.gatewayMethod")}:
                          </span>{" "}
                          <span className="capitalize">
                            {order.payment.method}
                          </span>
                        </div>
                        <div>
                          <span className="font-medium">
                            {t("admin.payouts.amount")}:
                          </span>{" "}
                          {order.currency || "$"}
                          {order.payment.amount.toFixed(2)}
                        </div>
                        {order.payment.transactionId && (
                          <div>
                            <span className="font-medium">
                              {t("admin.payments.transactionId")}:
                            </span>{" "}
                            <span className="font-mono text-xs">
                              {order.payment.transactionId}
                            </span>
                          </div>
                        )}
                        {order.payment.failureReason && (
                          <div className="text-red-600">
                            <span className="font-medium">
                              {t("admin.orderDetail.failureReason")}:
                            </span>{" "}
                            {order.payment.failureReason}
                          </div>
                        )}
                        {order.payment.refundedAmount && (
                          <div>
                            <span className="font-medium">
                              {t("admin.orderDetail.refundedAmount")}:
                            </span>{" "}
                            {order.currency || "$"}
                            {order.payment.refundedAmount.toFixed(2)}
                          </div>
                        )}
                        {order.payment.refundedAt && (
                          <div>
                            <span className="font-medium">
                              {t("admin.orderDetail.refundedAt")}:
                            </span>{" "}
                            {new Date(
                              order.payment.refundedAt,
                            ).toLocaleString()}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-gray-500 text-xs">
                        {order.paymentMethod === "cash_on_delivery" &&
                          t("admin.orderDetail.cashOnDeliveryNote")}
                        {order.paymentMethod === "pay_by_invoice" &&
                          t("admin.orderDetail.invoiceNote")}
                        {!order.paymentMethod &&
                          t("admin.orderDetail.noPaymentInfo")}
                      </div>
                    )}
                  </div>
                </div>
              </Stack>
            </Card>

            <Card padding="sm">
              <Stack spacing="sm">
                <div className="flex items-center justify-between">
                  <Heading size="sm" className="text-base">
                    Shipping & Tracking
                  </Heading>
                  {(isCarrierPluginEnabled &&
                    order.shippingMethod?.carrierProvider &&
                    order.shippingMethod.carrierProvider !== "manual" &&
                    order.status === "shipped") ||
                    (order.status == "processing" && (
                      <div className="flex gap-1.5">
                        {!order.shippingCarrierTrackingNumber && (
                          <button
                            onClick={handleGenerateShippingLabel}
                            disabled={generatingLabel}
                            className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
                            title={
                              t("admin.shipping.generateLabel") ||
                              "Generate Label"
                            }
                          >
                            {generatingLabel ? (
                              <Spinner size="sm" />
                            ) : (
                              <FaTag size={14} />
                            )}
                          </button>
                        )}
                        {order.shippingCarrierTrackingNumber && (
                          <>
                            <button
                              onClick={handleSyncTracking}
                              disabled={syncingTracking}
                              className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
                              title={
                                t("admin.shipping.syncTracking") ||
                                "Sync Tracking"
                              }
                            >
                              {syncingTracking ? (
                                <Spinner size="sm" />
                              ) : (
                                <FaSync size={14} />
                              )}
                            </button>
                            {order.shippingLabelKey && (
                              <button
                                onClick={handleDownloadShippingLabel}
                                disabled={downloadingLabel}
                                className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
                                title={
                                  t("admin.shipping.downloadLabel") ||
                                  "Download Label"
                                }
                              >
                                {downloadingLabel ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <FaDownload size={14} />
                                )}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                </div>

                {/* Shipping Status Badge */}
                {shippingStatus && (
                  <div className={`rounded-lg border-2 p-4 mb-3 ${getShippingStatusColor(shippingStatus.status)}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="text-sm font-semibold mb-1">
                          Shipping Status
                        </div>
                        <div className="text-base font-medium mb-1">
                          {shippingStatus.description}
                        </div>
                        {shippingStatus.location && (
                          <div className="text-xs opacity-75 mt-1">
                            📍 {shippingStatus.location}
                          </div>
                        )}
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${getShippingStatusColor(shippingStatus.status)}`}>
                        {shippingStatus.status.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={trackingNumber}
                      onChange={(e) => setTrackingNumber(e.target.value)}
                      placeholder={
                        t("admin.orderDetail.trackingNumberPlaceholder") ||
                        "Manual tracking number"
                      }
                      className="flex-1"
                    />
                    <button
                      onClick={handleUpdateTracking}
                      disabled={updating}
                      className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50 flex items-center justify-center"
                      title="Update tracking number"
                    >
                      {updating ? <Spinner size="sm" /> : <FaCheck size={14} />}
                    </button>
                  </div>
                  {order.shippingMethod && (
                    <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-100">
                      <span className="text-gray-600">Method</span>
                      <span className="text-gray-900">
                        {order.shippingMethod.name}
                      </span>
                    </div>
                  )}
                  {order.shippingCarrierProvider && (
                    <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-100">
                      <span className="text-gray-600">Carrier</span>
                      <span className="text-gray-900 font-medium">
                        {getCarrierName(order.shippingCarrierProvider)}
                      </span>
                    </div>
                  )}
                  {(order.trackingNumber ||
                    order.shippingCarrierTrackingNumber) && (
                    <div className="flex items-center justify-between text-sm pt-1 border-t border-gray-100">
                      <span className="text-gray-600">Tracking</span>
                      <span className="font-mono text-gray-900 text-xs">
                        {order.shippingCarrierTrackingNumber ||
                          order.trackingNumber}
                      </span>
                    </div>
                  )}
                </div>
                {order.trackingEvents && order.trackingEvents.length > 0 ? (
                  <div className="pt-3 mt-2 border-t border-gray-200">
                    <TrackingTimeline
                      events={order.trackingEvents}
                      currentStatus={order.status}
                    />
                  </div>
                ) : order.shippingCarrierTrackingNumber ? (
                  <div className="pt-3 mt-2 border-t border-gray-200">
                    <div className="text-sm text-gray-500 text-center py-4">
                      No tracking events yet. Click "Sync Tracking" to fetch the latest status from the carrier.
                    </div>
                  </div>
                ) : null}
              </Stack>
            </Card>

            <Card padding="sm">
              <Stack spacing="sm">
                <div className="flex items-center justify-between">
                  <Heading size="sm" className="text-base">
                    {t("admin.orderDetail.orderNotes")}
                  </Heading>
                  <button
                    onClick={handleUpdateNotes}
                    disabled={updating}
                    className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300 disabled:opacity-50"
                    title={t("admin.orderDetail.saveNotes") || "Save Notes"}
                  >
                    {updating ? <Spinner size="sm" /> : <FaSave size={14} />}
                  </button>
                </div>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 text-sm"
                  rows={3}
                  placeholder={t("admin.orderDetail.orderNotesPlaceholder")}
                />
              </Stack>
            </Card>

            {/* Order Summary - Lower priority, moved to bottom */}
            <Card padding="sm">
              <Stack spacing="sm">
                <Heading size="sm" className="text-base">
                  {t("admin.orderDetail.orderSummary")}
                </Heading>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("admin.orderDetail.subtotal")}:
                    </span>
                    <span className="text-gray-900">
                      {order.currency || "$"}
                      {order.subtotal.toFixed(2)}
                    </span>
                  </div>
                  {/* Show product-level discounts if any */}
                  {order.items?.some((item: any) => {
                    const originalPrice = calculateOriginalPriceForOrderItem(
                      item.product,
                      item.selectedVariantOptions,
                      order.locationId,
                    );
                    return originalPrice > item.price;
                  }) && (
                    <div className="flex justify-between">
                      <span className="text-green-600">
                        {t("admin.orderDetail.itemDiscounts") ||
                          "Product Discounts"}
                        :
                      </span>
                      <span className="text-green-600">
                        -{order.currency || "$"}
                        {order.items
                          .filter((item: any) => {
                            const originalPrice =
                              calculateOriginalPriceForOrderItem(
                                item.product,
                                item.selectedVariantOptions,
                                order.locationId,
                              );
                            return originalPrice > item.price;
                          })
                          .reduce((sum: number, item: any) => {
                            const originalPrice =
                              calculateOriginalPriceForOrderItem(
                                item.product,
                                item.selectedVariantOptions,
                                order.locationId,
                              );
                            return (
                              sum + (originalPrice - item.price) * item.quantity
                            );
                          }, 0)
                          .toFixed(2)}
                      </span>
                    </div>
                  )}
                  {/* Show order-level discount if any */}
                  {order.discount && order.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-green-600">
                        {t("admin.orderDetail.orderDiscount") ||
                          "Order Discount"}
                        :
                      </span>
                      <span className="text-green-600">
                        -{order.currency || "$"}
                        {order.discount.toFixed(2)}
                        {order.discountType === "percentage" && (
                          <span className="text-xs ml-1">
                            ({order.discountValue || 0}%)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("admin.orderDetail.tax")}:
                    </span>
                    <span className="text-gray-900">
                      {order.currency || "$"}
                      {order.tax.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("admin.orderDetail.shipping")}:
                    </span>
                    <span className="text-gray-900">
                      {order.currency || "$"}
                      {order.shipping.toFixed(2)}
                    </span>
                  </div>
                  <hr className="my-1.5" />
                  <div className="flex justify-between font-semibold text-base">
                    <span>{t("admin.total")}:</span>
                    <span>
                      {order.currency || "$"}
                      {order.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </Stack>
            </Card>
          </div>
        </div>

        {/* Customization Viewers */}
        {isCustomizationPluginEnabled &&
          viewingCustomization &&
          viewingCustomization.type === "2d-print" &&
          customizationData && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-7xl w-full h-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b flex justify-between items-center">
                  <Heading size="lg">Customization Designer</Heading>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setViewingCustomization(null);
                      setCustomizationData(null);
                      setProductData(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
                <div className="flex-1 overflow-auto">
                  <PrintDesigner
                    productId={viewingCustomization.productId}
                    canvasSize={
                      customizationData.canvasSize ||
                      viewingCustomization.canvasSize ||
                      "mug"
                    }
                    initialElements={customizationData.elements || []}
                    initialBackgroundColor={
                      customizationData.backgroundColor || "#FFFFFF"
                    }
                    initialBackgroundImage={originalBackgroundImage}
                    draftCustomizationId={viewingCustomization.customizationId}
                    backgroundImages={backgroundImages}
                    availableShapes={availableShapes}
                    onCustomizationUpdated={async (customizationId) => {
                      // Refresh the order data to get the updated preview image
                      await fetchOrder();
                      // Also refresh the customization data in the viewer
                      try {
                        const updatedCustomization =
                          await productCustomizationApi.getCustomizationById(
                            customizationId,
                          );
                        if (updatedCustomization) {
                          setCustomizationData(updatedCustomization);
                        }
                      } catch (err) {
                        console.error(
                          "Failed to refresh customization data:",
                          err,
                        );
                      }
                    }}
                    onCancel={() => {
                      setViewingCustomization(null);
                      setCustomizationData(null);
                      setProductData(null);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        {isCustomizationPluginEnabled &&
          viewingCustomization &&
          viewingCustomization.type === "3d-print" &&
          customizationData && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg max-w-7xl w-full h-full max-h-[90vh] overflow-hidden flex flex-col">
                <div className="p-4 border-b flex justify-between items-center">
                  <Heading size="lg">3D Customization Designer</Heading>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setViewingCustomization(null);
                      setCustomizationData(null);
                      setProductData(null);
                    }}
                  >
                    Close
                  </Button>
                </div>
                <div className="flex-1 overflow-auto">
                  <ThreeDPrintDesigner
                    productId={viewingCustomization.productId}
                    initialElements={customizationData.elements || []}
                    initialBackgroundColor={
                      customizationData.backgroundColor || "#FFFFFF"
                    }
                    initialBackgroundDepth={
                      customizationData.threeDData?.backgroundDepth || 5
                    }
                    initialBackgroundForm={
                      customizationData.threeDData?.backgroundForm || "auto"
                    }
                    draftCustomizationId={viewingCustomization.customizationId}
                    onCustomizationUpdated={async (customizationId) => {
                      // Refresh the order data to get the updated preview image
                      await fetchOrder();
                      // Also refresh the customization data in the viewer
                      try {
                        const updatedCustomization =
                          await productCustomizationApi.getCustomizationById(
                            customizationId,
                          );
                        if (updatedCustomization) {
                          setCustomizationData(updatedCustomization);
                        }
                      } catch (err) {
                        console.error(
                          "Failed to refresh customization data:",
                          err,
                        );
                      }
                    }}
                    onCancel={() => {
                      setViewingCustomization(null);
                      setCustomizationData(null);
                      setProductData(null);
                    }}
                  />
                </div>
              </div>
            </div>
          )}
      </Stack>
    </Container>
  );
};

export default OrderDetail;
